import { Inject, Injectable } from '@angular/core';
import moment from 'moment';
import { EnvironmentInstanceToken, PaceEnvironment } from '../../interfaces/environment.interface';
import { JsonUserModel, UserModel, UserModelFromJson, isJsonUserModel } from '../../models/complex/user.model';
import { LocalStorageCommonService } from './local-storage-common.service';

const KEY_IS_INITIAL_START = 'isInitialStart';
const KEY_APP_UPGRADE_VERSION = 'AppUpgradeVersion';
const KEY_SALES_PLANT = 'SalesAndPlant';
const KEY_BOOT_STOCK_LAST_SYNC = 'bootStockLastSyncTime';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageAppStateService {
    public constructor(
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {}

    public getInitialStartFlag(): boolean {
        const isInitialStart = this.localStorageCommonService.storageGet(KEY_IS_INITIAL_START);

        // if a value is there, it cannot be the first start. So only return 'true' if no value is returned.
        return typeof isInitialStart === 'undefined';
    }

    public setInitialStartFlag(isInitialStart: boolean): void {
        this.localStorageCommonService.storageSet(KEY_IS_INITIAL_START, isInitialStart);
    }

    public getUserRole(): string {
        return this.getUserData()?.getUserRole() || '';
    }

    public getUserData(): UserModel | undefined {
        const userData = this.localStorageCommonService.storageGet(KEY_SALES_PLANT);

        // TODO: Migration logic. Remove once the next version after 2.72.0 goes live.

        if (
            typeof userData === 'object' &&
            userData !== null &&
            !(userData as Record<string, unknown>).hasOwnProperty('appId')
        ) {
            (userData as JsonUserModel).appId = this.environment.appId;
        }

        return isJsonUserModel(userData) ? new UserModelFromJson(userData) : undefined;
    }

    public setUserData(selectedUserModel: UserModel): void {
        this.localStorageCommonService.storageSet(KEY_SALES_PLANT, selectedUserModel);
    }

    /**
     * Return the version number of the last successful offline store upgrade.
     */
    public getLastUpgradeVersion(): string {
        const lastUpgradeVersion = this.localStorageCommonService.storageGet<string>(KEY_APP_UPGRADE_VERSION);
        return typeof lastUpgradeVersion === 'string' ? lastUpgradeVersion : '';
    }

    /**
     * Set the version number of the last successful offline store upgrade.
     *
     * @param version
     */
    public setLastUpgradeVersion(version: string): void {
        this.localStorageCommonService.storageSet(KEY_APP_UPGRADE_VERSION, version);
    }

    public saveLastSyncBootStockTime(lastSyncBootStockTime: moment.Moment): void {
        this.localStorageCommonService.storageSet(KEY_BOOT_STOCK_LAST_SYNC, lastSyncBootStockTime.toISOString());
    }

    public getLastSyncBootStockTime(): moment.Moment {
        const lastSyncBootStockTime = this.localStorageCommonService.storageGet<string | undefined>(
            KEY_BOOT_STOCK_LAST_SYNC,
        );
        return typeof lastSyncBootStockTime === 'string' ? moment(lastSyncBootStockTime) : moment.invalid();
    }
}
